import { MetaDot } from "@/components/ui/meta-dot";
import { IconMagnifyingGlass } from "@central-icons-react/square-outlined-radius-0-stroke-1.5";
import { IconCheckOutline18, IconChevronDownOutline18, IconXmarkOutline18 } from "nucleo-ui-outline-18";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { useCallback, useEffect, useRef, useState } from "react";
import { fetchQBAccounts, type QBAccount } from "./quickbooksAccounts";
interface QuickBooksAccountSelectorProps {
  label: string;
  description?: string;
  headerRight?: React.ReactNode;
  value: string;
  onChange: (value: string) => void;
  required?: boolean;
  connectorId?: string;
  connectorType?: string;
}
function formatQuickBooksLabel(value?: string): string {
  if (!value) return "Other";
  return value.replace(/([a-z0-9])([A-Z])/g, "$1 $2").replace(/[_-]+/g, " ").replace(/\s+/g, " ").trim().replace(/\b\w/g, char => char.toUpperCase());
}
function getAccountTypeLabel(account: QBAccount): string {
  if (account.AccountSubType) return formatQuickBooksLabel(account.AccountSubType);
  return formatQuickBooksLabel(account.AccountType ?? "Account");
}
function getAccountGroupLabel(account: QBAccount): string {
  return formatQuickBooksLabel(account.AccountType ?? "Other");
}
function matchesAccountQuery(account: QBAccount, query: string): boolean {
  const normalizedQuery = query.trim().toLowerCase();
  if (!normalizedQuery) return true;
  return [account.Name, account.Id, account.AccountType, account.AccountSubType].filter(Boolean).some(value => value?.toLowerCase().includes(normalizedQuery));
}
export default function QuickBooksAccountSelector({
  label,
  description,
  headerRight,
  value,
  onChange,
  required,
  connectorId,
  connectorType
}: QuickBooksAccountSelectorProps) {
  const [accounts, setAccounts] = useState<QBAccount[]>([]);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [manualMode, setManualMode] = useState(false);
  const [fetchError, setFetchError] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>({});
  const containerRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const fetchedConnectorRef = useRef<string | null>(null);
  const fetchAccounts = useCallback(async () => {
    if (!connectorId || !connectorType) {
      setManualMode(true);
      return;
    }
    if (fetchedConnectorRef.current === connectorId) return;
    setLoading(true);
    setFetchError(false);
    try {
      const result = await fetchQBAccounts(connectorId, connectorType);
      setAccounts(result);
      setExpandedGroups({});
      fetchedConnectorRef.current = connectorId;
      setManualMode(false);
    } catch {
      setFetchError(true);
      setManualMode(true);
    } finally {
      setLoading(false);
    }
  }, [connectorId, connectorType]);
  useEffect(() => {
    if (connectorId && connectorType) {
      if (fetchedConnectorRef.current !== connectorId) {
        fetchedConnectorRef.current = null;
        setAccounts([]);
        setExpandedGroups({});
        setQuery("");
        setIsOpen(false);
      }
      fetchAccounts();
    } else {
      setManualMode(true);
    }
  }, [connectorId, connectorType, fetchAccounts]);
  const closeSelector = useCallback(() => {
    setIsOpen(false);
    setQuery("");
  }, []);
  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        closeSelector();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [closeSelector, isOpen]);
  useEffect(() => {
    if (!isOpen) return;
    const animationFrame = window.requestAnimationFrame(() => {
      searchInputRef.current?.focus();
    });
    return () => window.cancelAnimationFrame(animationFrame);
  }, [isOpen]);
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        closeSelector();
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [closeSelector, isOpen]);
  const findAccount = (id: string) => accounts.find(a => a.Id === id);
  const selectedAccount = value ? findAccount(value) : null;
  const selectedGroup = selectedAccount ? getAccountGroupLabel(selectedAccount) : null;
  const handleSelect = (accountId: string) => {
    onChange(accountId);
    closeSelector();
  };
  const toggleGroup = (groupName: string) => {
    setExpandedGroups(current => ({
      ...current,
      [groupName]: !current[groupName]
    }));
  };
  const groupedAccounts: Record<string, QBAccount[]> = {};
  for (const account of accounts) {
    const groupName_0 = getAccountGroupLabel(account);
    if (!groupedAccounts[groupName_0]) groupedAccounts[groupName_0] = [];
    groupedAccounts[groupName_0].push(account);
  }
  for (const groupName_1 of Object.keys(groupedAccounts)) {
    groupedAccounts[groupName_1].sort((a_0, b) => a_0.Name.localeCompare(b.Name));
  }
  const orderedGroups = Object.entries(groupedAccounts).sort(([groupA], [groupB]) => {
    if (selectedGroup && groupA === selectedGroup) return -1;
    if (selectedGroup && groupB === selectedGroup) return 1;
    return groupA.localeCompare(groupB);
  });
  const visibleGroups = orderedGroups.map(([groupName_2, groupAccounts]) => [groupName_2, query ? groupAccounts.filter(account_0 => matchesAccountQuery(account_0, query)) : groupAccounts] as const).filter(([, groupAccounts_0]) => groupAccounts_0.length > 0);
  const matchingAccountsCount = visibleGroups.reduce((count, [, groupAccounts_1]) => count + groupAccounts_1.length, 0);
  const allGroupsExpanded = orderedGroups.length > 0 && orderedGroups.every(([groupName_3]) => expandedGroups[groupName_3] === true);
  const toggleAllGroups = () => {
    if (allGroupsExpanded) {
      setExpandedGroups({});
      return;
    }
    setExpandedGroups(Object.fromEntries(orderedGroups.map(([groupName_4]) => [groupName_4, true])) as Record<string, boolean>);
  };

  // Manual input mode
  if (manualMode) {
    return <div className="space-y-2">
        {label && <div className="flex items-center justify-between gap-3">
            <label className="field-label block">
              {label}
              {required && <span className="ml-1 text-red-500">*</span>}
            </label>
            {headerRight}
          </div>}
        {description && <p className="text-ds-text-subtle dark:text-ds-text-disabled text-xs">{description}</p>}
        <Input type="text" value={typeof value === "string" ? value : ""} onChange={e_0 => onChange(e_0.target.value)} placeholder="Enter QuickBooks account ID" required={required} className="text-ds-text-primary" />
        {connectorId && !fetchError && accounts.length > 0 && <button type="button" onClick={() => setManualMode(false)} className="text-xs text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300">
            Select from accounts
          </button>}
        {fetchError && <p className="text-ds-text-disabled text-xs">Could not load accounts. Enter account ID manually.</p>}
      </div>;
  }
  return <div className="space-y-2" ref={containerRef}>
      {label && <div className="flex items-center justify-between gap-3">
          <label className="field-label block">
            {label}
            {required && <span className="ml-1 text-red-500">*</span>}
          </label>
          {headerRight}
        </div>}
      {description && <p className="text-ds-text-subtle dark:text-ds-text-disabled text-xs">{description}</p>}
      <div className="relative">
        <button type="button" onClick={() => {
        if (isOpen) {
          closeSelector();
          return;
        }
        setIsOpen(true);
      }} aria-expanded={isOpen} className={`bg-ds-surface-card flex w-full items-center justify-between gap-3 rounded-lg border px-3 py-2 text-left text-sm transition-[border-color,box-shadow,transform] duration-200 ease-out hover:border-zinc-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 active:scale-[0.99] ${isOpen ? "border-ds-focus-brand dark:border-ds-focus-brand" : "border-ds-neutral-300 dark:border-ds-neutral-700 dark:hover:border-zinc-600"}`}>
          <div className="min-w-0 flex-1">
            {selectedAccount ? <div className="min-w-0 space-y-1">
                <div className="flex flex-wrap items-center gap-1 text-xs font-medium">
                  <span className="text-ds-focus-brand">{getAccountGroupLabel(selectedAccount)}</span>
                  <MetaDot />
                  <span className="text-ds-text-muted">{getAccountTypeLabel(selectedAccount)}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-ds-text-primary truncate font-medium">{selectedAccount.Name}</span>
                  <span className="text-ds-text-disabled dark:text-ds-text-muted shrink-0 text-xs">
                    #{selectedAccount.Id}
                  </span>
                </div>
              </div> : value ? <div className="min-w-0 space-y-1">
                <div className="text-ds-text-primary font-medium">Account ID {value}</div>
                <div className="text-ds-text-muted text-xs">Saved value not found in the current account list</div>
              </div> : <div className="space-y-1">
                <div className="text-ds-text-primary font-medium">Choose an expense account</div>
                <div className="text-ds-text-muted text-xs">
                  Browse collapsed categories or search by name, type, or ID
                </div>
              </div>}
          </div>

          <div className="flex items-center gap-2">
            <span className="bg-ds-neutral-100 dark:bg-ds-neutral-700/70 flex h-7 w-7 items-center justify-center rounded-full">
              <IconChevronDownOutline18 className={`text-ds-text-muted h-4 w-4 transition-transform duration-200 ease-out dark:text-zinc-300 ${isOpen ? "rotate-180" : ""}`} />
            </span>
          </div>
        </button>

        {isOpen && <div className="shadow-ds-float-mid border-ds-border-default bg-ds-surface-card absolute z-50 mt-2 w-full overflow-hidden rounded-lg border">
            <div className="border-ds-border-default bg-ds-surface-card border-b p-2">
              <div className="border-ds-border-default bg-ds-surface-card relative overflow-hidden rounded-lg border">
                <IconMagnifyingGlass className="text-ds-text-disabled pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2" />
                <Input ref={searchInputRef} type="text" value={query} onChange={e_1 => setQuery(e_1.target.value)} placeholder="Search name, subtype, or account ID" className="text-ds-text-primary bg-ds-surface-card border-0 py-2.5 pl-9 pr-9" />
                {query && <button type="button" onClick={() => setQuery("")} className="text-ds-text-disabled hover:bg-ds-surface-hover hover:text-ds-text-subtle dark:hover:bg-ds-neutral-800 absolute right-2 top-1/2 flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded-full transition-colors duration-150 dark:hover:text-zinc-200">
                    <IconXmarkOutline18 className="h-3.5 w-3.5" />
                  </button>}
              </div>

              <div className="text-ds-text-muted text-2xs mt-2 flex items-center justify-between gap-3 px-1">
                <span>
                  {query ? `${matchingAccountsCount} matches across ${visibleGroups.length} categor${visibleGroups.length === 1 ? "y" : "ies"}` : `${accounts.length} active accounts in ${orderedGroups.length} categor${orderedGroups.length === 1 ? "y" : "ies"}`}
                </span>
                {!query && orderedGroups.length > 0 && <button type="button" onClick={toggleAllGroups} className="text-ds-text-subtle hover:text-ds-text-primary font-medium transition-colors duration-150 dark:hover:text-white">
                    {allGroupsExpanded ? "Collapse all" : "Expand all"}
                  </button>}
              </div>
            </div>

            {loading ? <div className="text-ds-text-muted px-4 py-6 text-sm">Loading accounts...</div> : visibleGroups.length === 0 ? <div className="px-4 py-8 text-center">
                <div className="text-ds-text-secondary text-sm font-medium dark:text-zinc-200">
                  {query ? "No accounts match that search" : "No accounts found"}
                </div>
                <div className="label label-xs mt-1">
                  {query ? "Try a name, account ID, type, or subtype." : "Use manual mode if you already know the ID."}
                </div>
              </div> : <div className="max-h-[26rem] overflow-y-auto">
                {visibleGroups.map(([groupName_5, groupAccounts_2]) => {
            const isExpanded = query ? true : expandedGroups[groupName_5] === true;
            const totalInGroup = groupedAccounts[groupName_5]?.length ?? groupAccounts_2.length;
            const containsSelected = groupAccounts_2.some(account_1 => account_1.Id === value);
            return <div key={groupName_5} className="border-ds-border-default border-t first:border-t-0">
                      <button type="button" onClick={() => {
                if (!query) toggleGroup(groupName_5);
              }} className="dark:hover:bg-ds-neutral-800/70 hover:bg-ds-surface-hover flex w-full items-center gap-3 px-3 py-2.5 text-left transition-colors duration-150">
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2">
                            <span className="text-ds-text-primary truncate text-sm font-medium">{groupName_5}</span>
                            {containsSelected && <span className="text-ds-focus-brand text-2xs font-semibold uppercase tracking-[0.12em]">
                                Selected
                              </span>}
                          </div>
                          <div className="label label-xs mt-1">
                            {query ? `${groupAccounts_2.length} match${groupAccounts_2.length === 1 ? "" : "es"}` : `${totalInGroup} account${totalInGroup === 1 ? "" : "s"}`}
                          </div>
                        </div>

                        <div className="flex items-center gap-2">
                          <span className="border-ds-border-default text-ds-text-muted dark:bg-ds-neutral-800/80 text-2xs flex h-7 min-w-7 items-center justify-center rounded-full border bg-zinc-50 px-0 font-medium">
                            {query ? `${groupAccounts_2.length}/${totalInGroup}` : totalInGroup}
                          </span>
                          <IconChevronDownOutline18 className={`text-ds-text-disabled h-4 w-4 transition-transform duration-200 ease-out ${isExpanded ? "rotate-180" : ""}`} />
                        </div>
                      </button>
                      {isExpanded && <div className="border-ds-border-default bg-ds-surface-card border-t">
                          {groupAccounts_2.map(account_2 => {
                  const isSelected = value === account_2.Id;
                  return <button key={account_2.Id} type="button" onClick={() => handleSelect(account_2.Id)} className={`border-ds-neutral-100 dark:border-ds-neutral-800 flex w-full items-start gap-3 border-t px-3 py-2.5 text-left transition-[background-color,transform] duration-150 ease-out first:border-t-0 active:scale-[0.995] ${isSelected ? "bg-blue-50/60 text-blue-700 dark:bg-blue-900/20 dark:text-blue-300" : "text-ds-text-primary dark:hover:bg-ds-neutral-800 hover:bg-ds-surface-hover"}`}>
                                <MetaDot className={cn("mt-2 shrink-0", isSelected && "bg-blue-500")} />
                                <div className="min-w-0 flex-1">
                                  <div className="flex items-center gap-2">
                                    <span className="truncate text-sm font-medium">{account_2.Name}</span>
                                    {isSelected && <span className="text-ds-focus-brand text-2xs font-semibold uppercase tracking-[0.12em]">
                                        Current
                                      </span>}
                                  </div>
                                  <div className="text-ds-text-muted mt-1 flex flex-wrap items-center gap-2 text-xs">
                                    <span>{getAccountTypeLabel(account_2)}</span>
                                    <MetaDot />
                                    <span>ID {account_2.Id}</span>
                                  </div>
                                </div>
                                <div className="pt-0.5">
                                  {isSelected ? <IconCheckOutline18 className="h-4 w-4 text-blue-500" /> : <span className="block h-4 w-4" />}
                                </div>
                              </button>;
                })}
                        </div>}
                    </div>;
          })}
              </div>}
          </div>}
      </div>
      <button type="button" onClick={() => setManualMode(true)} className="text-ds-text-muted hover:text-ds-text-secondary text-xs dark:hover:text-zinc-300">
        Enter account ID manually
      </button>
    </div>;
}